import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Drawer, Stack, styled, Typography } from '@mui/material';
import { drawerWidth } from 'constants/chat';

import { DeleteIcon } from 'assets/icons/DeleteIcon';
import { IconButton } from 'components/base/IconButton';

import { useApi } from '../../hooks/useApi';

interface IChatListProps {
  open: boolean;
  setActiveChatId: (id: string) => void;
  activeChatId: string | null;
  agentId: string;
}

const MessageStack = styled(Stack)`
  padding: 4px;
  cursor: pointer;

  &.active {
    background: ${({ theme }) => theme.palette.neutral[50]};
    border-radius: 6px;
  }
`;

export const ChatList = ({ open, setActiveChatId, activeChatId, agentId }: IChatListProps) => {
  const { apiFetch } = useApi();
  const [chats, setChats] = useState([]);

  const fetchChats = async () => {
    const res = await apiFetch(`chat/${agentId}/chats`);

    const data = await res.json();

    setChats(data);
  };

  const deleteChat = async (id: string) => {
    try {
      const res = await apiFetch(`chat/delete/${id}`, {
        method: 'DELETE',
      });

      const data = await res.json();
      fetchChats();

      toast.success(data.message, {
        position: 'top-right',
        autoClose: 5000,
      });
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An unexpected error occurred';

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 5000,
      });
    }
  };

  useEffect(() => {
    (async () => {
      fetchChats();
    })();
  }, [agentId]);

  useEffect(() => {
    if (!activeChatId) {
      fetchChats();
    }
  }, [activeChatId]);

  return (
    <Drawer
      sx={{
        width: drawerWidth,
        height: '75vh',

        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          position: 'absolute',
          zIndex: 10,
        },
      }}
      variant="persistent"
      anchor="left"
      open={open}
    >
      <Stack
        spacing={1}
        sx={{
          pt: 2,
          pr: 2,
        }}
      >
        {chats.length > 0 &&
          chats.map((chat) => {
            const isActive = chat.id === activeChatId;
            return (
              <MessageStack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                key={chat.id}
                onClick={() => {
                  setActiveChatId(chat.id);
                }}
                className={isActive ? 'active' : ''}
              >
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 500,
                    fontSize: '18px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {chat.first_message}
                </Typography>

                {isActive && (
                  <IconButton
                    onClick={() => deleteChat(chat.id)}
                    sx={{
                      mt: 0,
                      border: 'none',
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </MessageStack>
            );
          })}
      </Stack>
    </Drawer>
  );
};
